import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import { usePilotMode } from "@/hooks/use-pilot-mode";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, UserPlus, Copy, Check, X, Crown, Shield, Loader2, ExternalLink, Zap } from "lucide-react";
import type { Workspace } from "@shared/schema";

function getInitials(email: string | null | undefined) {
  if (!email) return "?";
  return email.slice(0, 2).toUpperCase();
}


  function RoleBadge({ role }: { role: string }) {
    const configs: Record<string, { label: string; className: string }> = {
      owner: { label: "Owner", className: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
      admin: { label: "Admin", className: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" },
      editor: { label: "Editor", className: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
      viewer: { label: "Viewer", className: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
      marketing_team: { label: "Marketing Partner", className: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
      member: { label: "Member", className: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
    };
    const config = configs[role] || configs.viewer;
    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    );
  }
  
function MemberAvatar({ email }: { email: string | null | undefined }) {
  return (
    <div className="w-9 h-9 rounded-full bg-blue-600/20 border border-blue-500/20 flex items-center justify-center shrink-0">
      <span className="text-[11px] font-bold text-blue-400">{getInitials(email)}</span>
    </div>
  );
}

export default function TeamPage() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const pilotMode = usePilotMode(); // PILOT_MODE: no "Upgrade Plan" prompt
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addSeatOpen, setAddSeatOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [invitedEmail, setInvitedEmail] = useState("");
  const [invitedRole, setInvitedRole] = useState<"admin" | "editor" | "viewer">("viewer");
  const [seatEmail, setSeatEmail] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const { data: workspace } = useQuery<Workspace>({
    queryKey: ["/api/workspaces", activeWorkspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const { data: currentUser } = useQuery<any>({
    queryKey: ["/api/auth/user"],
    queryFn: async () => {
      const res = await fetch("/api/auth/user", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const { data: members = [], isLoading: membersLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "members"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/members`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const isOwner = workspace?.userId === currentUser?.id;
  const plan = currentUser?.subscription || "free";
  const isPaid = plan === "solo" || plan === "growth";

  const marketingTeamSeat = members.find(m => m.role === "marketing_team");
  const additionalSeats = members.filter(m => m.role === "member");

  const generateInviteMutation = useMutation({
    mutationFn: async ({ role, email }: { role: string; email: string }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/members`, {
        role,
        invitedEmail: email || null,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "members"] });
      setGeneratedLink(data.inviteUrl);
    },
    onError: (err: any) => {
      toast({ title: "Could not generate invite", description: err.message, variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (memberId: number) => {
      await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "members"] });
      toast({ title: "Member removed" });
    },
  });

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (!activeWorkspaceId) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="page-team">
        <p className="text-muted-foreground text-sm">No workspace selected.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="page-team">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Users size={24} className="text-blue-400" />
          Team
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Invite colleagues to collaborate on this workspace — they'll use their own account to access Drpr.ai.
        </p>
      </div>

      {!isPaid && pilotMode && (
        <Card className="p-6" data-testid="card-team-pilot-note">
          <p className="text-xs text-muted-foreground leading-relaxed">Inviting team members is not part of the pilot yet.</p>
        </Card>
      )}

      {!isPaid && !pilotMode && (
        <Card className="p-6" data-testid="card-upgrade-prompt">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center shrink-0">
              <Zap size={18} className="text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-1">Team collaboration is a paid feature</h3>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                Upgrade to <strong>Solo ($199.99/mo)</strong> to add 1 free marketing team connection plus additional seats at $29.99/seat.
                <br />
                Upgrade to <strong>Growth ($399.99/mo)</strong> for unlimited team members across all workspaces.
              </p>
              <Button size="sm" onClick={() => window.location.href = "/settings"} data-testid="button-upgrade-team">
                Upgrade Plan
              </Button>
            </div>
          </div>
        </Card>
      )}

      {isPaid && (
        <>
          <Card className="p-6 space-y-4" data-testid="card-marketing-team-seat">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Crown size={14} className="text-amber-400" />
                  Marketing Team Connection
                  <Badge variant="secondary" className="text-[10px]">Free</Badge>
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Connect your agency or marketing partner. They get full access to manage this workspace — strategy, content, leads, everything.
                </p>
              </div>
              {isOwner && !marketingTeamSeat && (
                <Button size="sm" onClick={() => { setInviteOpen(true); setGeneratedLink(null); }} data-testid="button-invite-marketing-team">
                  <UserPlus size={14} />
                  Invite
                </Button>
              )}
            </div>

            {marketingTeamSeat ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                <div className="flex items-center gap-3">
                  <MemberAvatar email={marketingTeamSeat.invitedEmail} />
                  <div>
                    <p className="text-sm font-medium">{marketingTeamSeat.invitedEmail || "Invited member"}</p>
                    <RoleBadge role={marketingTeamSeat.role} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={marketingTeamSeat.status === "accepted" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"} variant="outline">
                    {marketingTeamSeat.status === "accepted" ? "Active" : "Pending"}
                  </Badge>
                  {isOwner && (
                    <Button size="sm" variant="ghost" onClick={() => removeMutation.mutate(marketingTeamSeat.id)} disabled={removeMutation.isPending} data-testid={`button-remove-member-${marketingTeamSeat.id}`}>
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3 border border-border border-dashed">
                No marketing team connected yet. Invite your agency or marketing partner for free.
              </div>
            )}
          </Card>

          <Card className="p-6 space-y-4" data-testid="card-additional-seats">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Users size={14} className="text-blue-400" />
                  Additional Team Members
                  {plan === "solo" && <Badge variant="outline" className="text-[10px]">$29.99/seat/mo</Badge>}
                  {plan === "growth" && <Badge variant="secondary" className="text-[10px]">Unlimited</Badge>}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {plan === "growth" ? "Unlimited seats included in your Growth plan." : "Add team members — each extra seat is $29.99/month."}
                </p>
              </div>
              {isOwner && (
                <Button size="sm" onClick={() => { setAddSeatOpen(true); setGeneratedLink(null); }} data-testid="button-add-seat">
                  <UserPlus size={14} />
                  Add Seat
                </Button>
              )}
            </div>

            {membersLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : additionalSeats.length === 0 ? (
              <div className="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3 border border-border border-dashed">
                No additional seats yet. Add team members to collaborate on this workspace.
              </div>
            ) : (
              <div className="space-y-2">
                {additionalSeats.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border" data-testid={`row-member-${member.id}`}>
                    <div className="flex items-center gap-3">
                      <MemberAvatar email={member.invitedEmail} />
                      <div>
                        <p className="text-sm font-medium">{member.invitedEmail || "Invited member"}</p>
                        <RoleBadge role={member.role} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={member.status === "accepted" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"} variant="outline">
                        {member.status === "accepted" ? "Active" : "Pending"}
                      </Badge>
                      {isOwner && (
                        <Button size="sm" variant="ghost" onClick={() => removeMutation.mutate(member.id)} disabled={removeMutation.isPending} data-testid={`button-remove-member-${member.id}`}>
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) { setGeneratedLink(null); setInvitedEmail(""); } }}>
        <DialogContent data-testid="dialog-invite-marketing-team">
          <DialogHeader>
            <DialogTitle>Invite Marketing Team Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Your free marketing team seat — perfect for connecting with your agency, freelance marketer, or internal marketing partner. They get full workspace access.
            </p>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Their Email (optional label)</label>
              <input
                value={invitedEmail}
                onChange={e => setInvitedEmail(e.target.value)}
                placeholder="partner@agency.com"
                className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background"
                data-testid="input-invite-email"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Just for your reference — they'll log in with their own account</p>
            </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1.5">Permission Level</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["admin", "editor", "viewer"] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setInvitedRole(r)}
                      className={`px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                        invitedRole === r
                          ? "bg-blue-600/20 border-blue-500/30 text-blue-400"
                          : "bg-background border-border text-muted-foreground hover:bg-muted/50"
                      }`}
                      data-testid={`button-role-${r}`}
                    >
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

            {!generatedLink ? (
              <Button
                className="w-full"
                onClick={() => generateInviteMutation.mutate({ role: invitedRole, email: invitedEmail })}
                disabled={generateInviteMutation.isPending}
                data-testid="button-generate-invite-link"
              >
                {generateInviteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                Generate Invite Link
              </Button>
            ) : (
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground block">Share this link</label>
                <div className="flex gap-2">
                  <input readOnly value={generatedLink} className="flex-1 text-xs px-3 py-2 rounded-md border border-border bg-muted/30 font-mono" data-testid="input-invite-link" />
                  <Button size="sm" variant="outline" onClick={() => copyLink(generatedLink)} data-testid="button-copy-invite-link">
                    {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  They'll click this link, log in with their account, and get immediate access.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addSeatOpen} onOpenChange={(open) => { setAddSeatOpen(open); if (!open) { setGeneratedLink(null); setSeatEmail(""); } }}>
        <DialogContent data-testid="dialog-add-seat">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {plan === "solo" && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-400">Additional seats are billed at $29.99/seat/month, charged to your subscription.</p>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Their Email (optional label)</label>
              <input
                value={seatEmail}
                onChange={e => setSeatEmail(e.target.value)}
                placeholder="teammate@company.com"
                className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background"
                data-testid="input-seat-email"
              />
            </div>
            {!generatedLink ? (
              <Button
                className="w-full"
                onClick={() => generateInviteMutation.mutate({ role: invitedRole, email: seatEmail })}
                disabled={generateInviteMutation.isPending}
                data-testid="button-generate-seat-link"
              >
                {generateInviteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                Generate Invite Link
              </Button>
            ) : (
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground block">Share this link</label>
                <div className="flex gap-2">
                  <input readOnly value={generatedLink} className="flex-1 text-xs px-3 py-2 rounded-md border border-border bg-muted/30 font-mono" data-testid="input-seat-invite-link" />
                  <Button size="sm" variant="outline" onClick={() => copyLink(generatedLink)} data-testid="button-copy-seat-link">
                    {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  They'll click this link, log in, and accept the invitation to join this workspace.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
